import { useEffect, useState } from "react";
import { CheckCircle2, Loader2, Pencil } from "lucide-react";
import Markdown from "react-markdown";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const btn = "btn-brutal px-3 py-1.5 font-display uppercase font-bold text-xs inline-flex items-center gap-1.5 disabled:opacity-50";

export default function ChapterEditDialog({ entry, onClose, onSaved }) {
  const [form, setForm] = useState({ title: "", markdown: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (entry) setForm({ title: entry.title || "", markdown: entry.markdown || "" }); }, [entry]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/help/chapter-entries/${entry.id}`, form);
      toast.success("Paragraaf bijgewerkt in de handleiding.");
      onSaved?.(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  return (
    <Dialog open={!!entry} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl border-2 border-foreground rounded-none heavy-shadow max-h-[85vh] overflow-y-auto" data-testid="chapter-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-black uppercase tracking-tight pr-8 inline-flex items-center gap-2"><Pencil className="w-5 h-5" /> Paragraaf bewerken</DialogTitle>
          <DialogDescription>Wijzigingen gaan direct de handleiding in{entry?.chapter_title ? <> (hoofdstuk <i>{entry.chapter_title}</i>)</> : null}. Het anker <code>#{entry?.anchor}</code> blijft gelijk, dus bestaande links blijven werken.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <label className="block">
            <span className="font-display uppercase text-[10px] tracking-widest">Titel</span>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} maxLength={120} data-testid="chapter-edit-title"
              className="w-full border-2 border-foreground bg-background px-3 py-2 text-sm font-bold focus:outline-none focus:ring-2 focus:ring-primary" />
          </label>
          <label className="block">
            <span className="font-display uppercase text-[10px] tracking-widest">Tekst (Markdown)</span>
            <textarea value={form.markdown} onChange={(e) => setForm({ ...form, markdown: e.target.value })} rows={8} maxLength={8000} data-testid="chapter-edit-markdown"
              className="w-full border-2 border-foreground bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
          </label>
          <div className="border-2 border-dashed border-border p-3 text-sm" data-testid="chapter-edit-preview">
            <p className="font-bold">{form.title}</p>
            <div className="prose prose-sm max-w-none"><Markdown>{form.markdown}</Markdown></div>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className={`${btn} bg-background text-foreground`} data-testid="chapter-edit-cancel">Annuleren</button>
            <button onClick={save} disabled={saving || form.title.trim().length < 3 || form.markdown.trim().length < 3} className={`${btn} bg-primary text-primary-foreground`} data-testid="chapter-edit-save">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Opslaan
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
